import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomModal, { GridItem, GridSection } from 'src/components/CustomModal';
import {
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { useCreateTag, useUpdateTag, useTag } from '../hooks/useTags';
import { TagFormValues } from '../types';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import moment from 'moment';
import { Extension } from 'src/careflow/ph-core/fhir-client/resources/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { tagsValueSetConfig, getSystemUrlFromConfig, getCodeSystemColor } from '../config/valuesetConfig';

interface TagFormProps {
  open: boolean;
  tagId: string | null;
  onClose: () => void;
}

const validationSchema = Yup.object().shape({
  display: Yup.string().required('Display is required'),
  code: Yup.string().required('Code is required'),
  version: Yup.string(),
  description: Yup.string(),
  comments: Yup.string(),
  resources: Yup.array().of(Yup.string()).min(1, 'At least one resource is required'),
});

// Use configuration for resource types
const RESOURCE_OPTIONS = tagsValueSetConfig.resourceTypes.map((rt) => rt.code);

export default function TagForm({ open, tagId, onClose }: TagFormProps) {
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();
  const { data: existingTag } = useTag(tagId || null);
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();

  // Get organization name from auth context
  const organizationName = auth.getSelectedOrg?.()?.name || 'Organization Tags';

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TagFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      display: '',
      code: '',
      version: '',
      description: '',
      comments: '',
      resources: [],
    },
  });

  useEffect(() => {
    if (existingTag && tagId) {
      const concepts = existingTag.compose?.include?.[0]?.concept || [];
      const firstConcept = concepts[0];

      // Extract resources from meta.tag
      const resources: string[] = [];
      if (existingTag.meta?.tag) {
        existingTag.meta.tag.forEach((tagMeta: any) => {
          if (tagMeta.code && RESOURCE_OPTIONS.includes(tagMeta.code)) {
            resources.push(tagMeta.code);
          }
        });
      }

      // Extract comments from extensions
      const getExtensionValue = (url: string) => {
        return existingTag.meta?.extension?.find((ext: any) => ext.url === url)?.valueString || '';
      };

      reset({
        display: firstConcept?.display || '',
        code: firstConcept?.code || '',
        version: existingTag.version || '',
        description: existingTag.description || '',
        comments: getExtensionValue('comments'),
        resources,
      });
    } else {
      reset({
        display: '',
        code: '',
        version: '',
        description: '',
        comments: '',
        resources: [],
      });
    }
  }, [existingTag, tagId, reset]);

  const onSubmit = async (data: TagFormValues) => {
    try {
      console.log('Submitting tag data:', data); // Debug log
      
      const tagPayload = {
        display: data.display,
        code: data.code,
        resources: data.resources,
        description: data.description,
        comments: data.comments,
      };

      console.log('Tag payload:', tagPayload); // Debug log

      if (tagId) {
        await updateTag.mutateAsync({ id: tagId, tag: tagPayload });
        enqueueSnackbar('Tag updated successfully', { variant: 'success' });
      } else {
        await createTag.mutateAsync(tagPayload);
        enqueueSnackbar('Tag created successfully', { variant: 'success' });
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving tag:', error); // Debug log
      const errorMessage = error?.response?.data?.message || error?.message || 'Error saving tag';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleSave = async () => {
    // Trigger validation and submission
    await handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={tagId ? 'Edit Tag' : 'Add New Tag'}
      breadcrumbs={['Tags List', tagId ? 'Edit Tag' : 'Add New Tag']}
      onSave={handleSave}
      onCancel={handleCancel}
      isLoading={createTag.isLoading || updateTag.isLoading}
      saveButtonTitle={tagId ? 'Update Tag' : 'Add Tag'}
      containerSx={{
        width: '40vw',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ my: 3, width: '100%' }}>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {/* Row 1: Code System (read-only) and Resources */}
            <Grid item xs={6}>
              <TextField
                label="Code System"
                value={organizationName}
                fullWidth
                disabled
                helperText="Auto-managed by organization"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="resources"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.resources}>
                    <InputLabel>Resources</InputLabel>
                    <Select
                      {...field}
                      multiple
                      label="Resources"
                      placeholder="Select Resources..."
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {RESOURCE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.resources && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {(() => {
                          const error = errors.resources;
                          if (Array.isArray(error)) {
                            return error[0]?.message || 'At least one resource is required';
                          }
                          return (error as { message?: string })?.message || 'At least one resource is required';
                        })()}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Row 2: Display and Code */}
            <Grid item xs={6}>
              <TextField
                {...register('display')}
                label="Display"
                placeholder="Write Display..."
                fullWidth
                error={!!errors.display}
                helperText={errors.display?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                {...register('code')}
                label="Code"
                placeholder="Write Code..."
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            </Grid>

            {/* Optional fields for edit mode */}
            {tagId && (
              <>
                <Grid item xs={12}>
                  <TextField
                    {...register('version')}
                    label="Version"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    {...register('description')}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    {...register('comments')}
                    label="Comments"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </form>
    </CustomModal>
  );
}

